import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { api, getToken, setToken, clearToken } from './api';

// Auth Context
const AuthContext = createContext();
export function useAuth() { return useContext(AuthContext); }

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (getToken()) {
      api.getMe().then(setUser).catch(() => clearToken()).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const data = await api.login({ email, password });
    setToken(data.token);
    setUser(data.user);
  };

  const register = async (formData) => {
    const data = await api.register(formData);
    setToken(data.token);
    setUser(data.user);
  };

  const logout = () => { clearToken(); setUser(null); };

  if (loading) return <div className="app-wrapper"><div className="spinner"></div></div>;

  return (
    <AuthContext.Provider value={{ user, login, register, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// Logo component
function Logo() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="16" y="8" width="32" height="48" rx="4" fill="#d4760a"/>
      <line x1="24" y1="20" x2="40" y2="20" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      <line x1="24" y1="28" x2="40" y2="28" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      <line x1="24" y1="36" x2="34" y2="36" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      <path d="M44 30 L48 6 L50 6 L46 30Z" fill="#555"/>
      <path d="M48 6 L49 2 L50 6Z" fill="#d4760a"/>
    </svg>
  );
}

// Header
function Header() {
  const { user, logout } = useAuth();
  return (
    <header className="header">
      <div className="header-inner">
        <Link to="/" className="logo"><Logo /><span className="logo-text">TipNotes</span></Link>
        <nav className="header-nav">
          {user ? (
            <>
              <Link to="/account" className="nav-user-name" style={{textDecoration:"none"}}>Hi, {user.firstName}!</Link>
              <Link to="/profiles" className="nav-link">My Profiles</Link>
              {user.isAdmin && <Link to="/admin" className="nav-link" style={{color:"var(--amber-dark)",fontWeight:700}}>⚙️ Admin</Link>}
              <button onClick={logout} className="nav-link">Log Out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link">Log In</Link>
              <Link to="/register" className="nav-link nav-link-primary">Sign Up</Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

// Home Page
function Home() {
  const { user } = useAuth();
  return (
    <div className="hero">
      <h1>Dine Your Way,<br/>Every Time</h1>
      <p>Share your dining preferences with your server before they even approach the table. Better service, better tips, better experience.</p>
      {user ? (
        <Link to="/profiles" className="btn btn-primary">Go to My Profiles</Link>
      ) : (
        <Link to="/register" className="btn btn-primary">Create Your Profile — Free</Link>
      )}
      <div className="hero-features">
        <div className="hero-feature">
          <div className="hero-feature-icon">📋</div>
          <div className="hero-feature-title">Set Preferences</div>
          <div className="hero-feature-desc">Pace, check-ins, conversation style, allergens, and more</div>
        </div>
        <div className="hero-feature">
          <div className="hero-feature-icon">📱</div>
          <div className="hero-feature-title">Share Easily</div>
          <div className="hero-feature-desc">QR code, link, printed card, or show on your phone</div>
        </div>
        <div className="hero-feature">
          <div className="hero-feature-icon">⭐</div>
          <div className="hero-feature-title">Better Experience</div>
          <div className="hero-feature-desc">Your server knows exactly how to take care of you</div>
        </div>
      </div>
    </div>
  );
}

// Login
function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <div style={{maxWidth:400,margin:'40px auto'}}>
      <div className="card">
        <h2 className="card-title">Welcome Back</h2>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required />
          </div>
          <button className="btn btn-primary btn-block" disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</button>
        </form>
        <p style={{textAlign:'center',marginTop:16,fontSize:'0.9rem'}}>
          <Link to="/forgot-password" style={{color:'var(--amber)'}}>Forgot password?</Link>
        </p>
        <p style={{textAlign:'center',marginTop:8,fontSize:'0.9rem',color:'var(--gray)'}}>
          Don't have an account? <Link to="/register" style={{color:'var(--amber)',fontWeight:600}}>Sign up</Link>
        </p>
      </div>
    </div>
  );
}

// Register
function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName:'', lastName:'', email:'', password:'', username:'' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await register(form);
      navigate('/');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const update = (k,v) => setForm(f=>({...f,[k]:v}));

  const checkUsername = async (val) => {
    if (!val || val.length < 3) { setUsernameStatus(null); return; }
    if (!/^[a-z0-9_-]{3,30}$/i.test(val)) { setUsernameStatus('invalid'); return; }
    setUsernameStatus('checking');
    try {
      const res = await api.checkUsername(val);
      setUsernameStatus(res.available ? 'available' : 'taken');
    } catch { setUsernameStatus(null); }
  };

  return (
    <div style={{maxWidth:400,margin:'40px auto'}}>
      <div className="card">
        <h2 className="card-title">Create Account</h2>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input className="form-input" value={form.firstName} onChange={e=>update('firstName',e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input className="form-input" value={form.lastName} onChange={e=>update('lastName',e.target.value)} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Username <span style={{color:'var(--gray)',fontWeight:400}}>(optional)</span></label>
            <input className="form-input" value={form.username}
              onChange={e=>{update('username',e.target.value.toLowerCase());checkUsername(e.target.value);}}
              placeholder="e.g. mark" maxLength={30} />
            {usernameStatus === 'checking' && <div className="form-hint">Checking...</div>}
            {usernameStatus === 'available' && <div className="form-hint" style={{color:'var(--green)'}}>✓ tipnotes.com/{form.username} is available!</div>}
            {usernameStatus === 'taken' && <div className="form-error">Username already taken</div>}
            {usernameStatus === 'invalid' && <div className="form-error">3–30 characters, letters/numbers/hyphens/underscores only</div>}
            {!usernameStatus && <div className="form-hint">Creates your profile URL: tipnotes.com/username/profile-name</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={e=>update('email',e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" value={form.password} onChange={e=>update('password',e.target.value)} required minLength={8} />
            <div className="form-hint">At least 8 characters</div>
          </div>
          <button className="btn btn-primary btn-block" disabled={loading || usernameStatus === 'taken' || usernameStatus === 'invalid'}>{loading ? 'Creating...' : 'Create Account'}</button>
        </form>
        <p style={{textAlign:'center',marginTop:16,fontSize:'0.9rem',color:'var(--gray)'}}>
          Already have an account? <Link to="/login" style={{color:'var(--amber)',fontWeight:600}}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

// Forgot Password
function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    try { await api.forgotPassword(email); setSent(true); }
    catch (err) { setError(err.message); }
  };

  if (sent) return (
    <div style={{maxWidth:400,margin:'40px auto'}}>
      <div className="card" style={{textAlign:'center'}}>
        <div style={{fontSize:'2.5rem',marginBottom:12}}>📧</div>
        <h2 className="card-title">Check Your Email</h2>
        <p style={{color:'var(--gray)'}}>If an account with that email exists, we've sent a password reset link.</p>
      </div>
    </div>
  );

  return (
    <div style={{maxWidth:400,margin:'40px auto'}}>
      <div className="card">
        <h2 className="card-title">Reset Password</h2>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required />
          </div>
          <button className="btn btn-primary btn-block">Send Reset Link</button>
        </form>
      </div>
    </div>
  );
}

// Reset Password
function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (password !== confirm) return setError('Passwords do not match');
    try {
      await api.resetPassword(searchParams.get('token'), password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) { setError(err.message); }
  };

  return (
    <div style={{maxWidth:400,margin:'40px auto'}}>
      <div className="card">
        <h2 className="card-title">Set New Password</h2>
        {success && <div className="alert alert-success">Password reset! Redirecting to login...</div>}
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input className="form-input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required minLength={8} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input className="form-input" type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} required />
          </div>
          <button className="btn btn-primary btn-block">Reset Password</button>
        </form>
      </div>
    </div>
  );
}

// Verify Email Page
function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      api.verifyEmail(token).then(() => setStatus('success')).catch(() => setStatus('error'));
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  return (
    <div style={{maxWidth:400,margin:'60px auto',textAlign:'center'}}>
      <div className="card">
        {status === 'verifying' && <><div className="spinner"></div><p>Verifying your email...</p></>}
        {status === 'success' && <><div style={{fontSize:'3rem'}}>✅</div><h2 className="card-title">Email Verified!</h2><p style={{color:'var(--gray)'}}>Your account is now fully activated.</p><Link to="/profiles" className="btn btn-primary" style={{marginTop:16}}>Go to My Profiles</Link></>}
        {status === 'error' && <><div style={{fontSize:'3rem'}}>❌</div><h2 className="card-title">Verification Failed</h2><p style={{color:'var(--gray)'}}>This link may be expired or invalid.</p><Link to="/profiles" className="btn btn-secondary" style={{marginTop:16}}>Go to My Profiles</Link></>}
      </div>
    </div>
  );
}

// Dashboard
function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.getProfiles(), api.getMe()])
      .then(([p, u]) => { setProfiles(p); setUser(u); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      {!user.emailVerified && (
        <div className="alert alert-info">
          📧 Please verify your email to unlock all features.{' '}
          <button onClick={async()=>{try{await api.resendVerification();alert('Verification email sent!')}catch(e){alert(e.message)}}}
            style={{background:'none',border:'none',color:'var(--amber)',fontWeight:700,cursor:'pointer',textDecoration:'underline'}}>
            Resend verification
          </button>
        </div>
      )}

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24}}>
        <h1 style={{fontFamily:'var(--font-display)',fontSize:'1.8rem'}}>My Profiles</h1>
        <button className="btn btn-primary" onClick={()=>navigate('/profile/new')}>+ New Profile</button>
      </div>

      {profiles.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h2>No Profiles Yet</h2>
          <p>Create your first dining preference profile to share with servers.</p>
          <button className="btn btn-primary" onClick={()=>navigate('/profile/new')}>Create Profile</button>
        </div>
      ) : (
        profiles.map(p => (
          <div key={p.id} className="profile-item" onClick={()=>navigate(`/profile/${p.id}`)}>
            <div className="profile-item-info">
              <h3>{p.profile_name}</h3>
              <p>{p.display_name} — {p.pace} pace, {p.conversation_style} conversation</p>
            </div>
            <div className="profile-item-meta">
              <div className="profile-item-views">{p.view_count || 0} views</div>
              <div>{p.is_active ? '🟢 Active' : '⚪ Inactive'}</div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// Profile Editor
const ALLERGENS = ['Peanuts','Tree Nuts','Dairy','Eggs','Wheat/Gluten','Soy','Fish','Shellfish','Sesame'];
const SEATING_OPTIONS = [
  {v:'booth', icon:'🪑', label:'Booth'},
  {v:'table', icon:'🍽️', label:'Table'},
  {v:'bar', icon:'🍸', label:'Bar'},
  {v:'patio', icon:'🌿', label:'Patio/Outside'},
  {v:'window', icon:'🪟', label:'Window Seat'},
  {v:'quiet', icon:'🔇', label:'Quiet Corner'},
  {v:'away-kitchen', icon:'🚫', label:'Away from Kitchen'},
  {v:'away-bathroom', icon:'🚽', label:'Away from Bathrooms'},
  {v:'high-top', icon:'🪑', label:'High Top'},
  {v:'accessible', icon:'♿', label:'Accessible Seating'},
];

function ProfileEditor() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams();
  const location = window.location.pathname;
  const isNew = !editId || location === '/profile/new';

  const [form, setForm] = useState({
    profileName:'My Profile', displayName:'', pace:'moderate',
    checkInFrequency:'normal', conversationStyle:'friendly',
    allergens:[], dietaryNotes:'', customNotes:'',
    seatingPreferences:[], hostNotes:''
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isNew) {
      api.getProfile(editId).then(p => {
        setForm({
          profileName: p.profile_name, displayName: p.display_name, pace: p.pace,
          checkInFrequency: p.check_in_frequency, conversationStyle: p.conversation_style,
          allergens: p.allergens || [], dietaryNotes: p.dietary_notes || '',
          customNotes: p.custom_notes || '',
          seatingPreferences: p.seating_preferences || [], hostNotes: p.host_notes || ''
        });
      }).catch(()=>navigate('/profiles')).finally(()=>setLoading(false));
    }
  }, [editId, isNew, navigate]);

  const update = (k,v) => setForm(f=>({...f,[k]:v}));

  const toggleAllergen = (a) => {
    setForm(f => ({...f, allergens: f.allergens.includes(a) ? f.allergens.filter(x=>x!==a) : [...f.allergens, a]}));
  };

  const toggleSeating = (s) => {
    setForm(f => ({...f, seatingPreferences: f.seatingPreferences.includes(s) ? f.seatingPreferences.filter(x=>x!==s) : [...f.seatingPreferences, s]}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      if (isNew) {
        const created = await api.createProfile(form);
        navigate(`/profile/${created.id}`);
      } else {
        await api.updateProfile(editId, form);
        navigate(`/profile/${editId}`);
      }
    } catch (err) { setError(err.message); setSaving(false); }
  };

  if (loading) return <div className="spinner"></div>;

  return (
    <div style={{maxWidth:560,margin:'0 auto'}}>
      <button className="btn btn-secondary btn-sm" onClick={()=>navigate(isNew?'/profiles':`/profile/${editId}`)} style={{marginBottom:16}}>
        ← Back
      </button>
      <div className="card">
        <h2 className="card-title">{isNew ? 'Create Profile' : 'Edit Profile'}</h2>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Profile Name</label>
            <input className="form-input" value={form.profileName} onChange={e=>update('profileName',e.target.value)} placeholder="e.g. Casual Dinner, Business Lunch" />
            <div className="form-hint">For your reference only — servers won't see this</div>
          </div>

          <div className="form-group">
            <label className="form-label">Display Name</label>
            <input className="form-input" value={form.displayName} onChange={e=>update('displayName',e.target.value)} placeholder="How your server should address you" required />
          </div>

          <div className="form-group">
            <label className="form-label">Dining Pace</label>
            <div className="option-cards">
              {[{v:'relaxed',icon:'🐢',label:'Relaxed',desc:'No rush'},{v:'moderate',icon:'🍽️',label:'Moderate',desc:'Normal pace'},{v:'quick',icon:'⚡',label:'Quick',desc:'Time-conscious'}].map(o=>(
                <div key={o.v} className={`option-card ${form.pace===o.v?'active':''}`} onClick={()=>update('pace',o.v)}>
                  <div className="option-card-icon">{o.icon}</div>
                  <div className="option-card-label">{o.label}</div>
                  <div className="option-card-desc">{o.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Check-in Frequency</label>
            <div className="option-cards">
              {[{v:'minimal',icon:'🤫',label:'Minimal',desc:'Only when needed'},{v:'normal',icon:'👋',label:'Normal',desc:'Standard check-ins'},{v:'frequent',icon:'🔔',label:'Frequent',desc:'Check often'}].map(o=>(
                <div key={o.v} className={`option-card ${form.checkInFrequency===o.v?'active':''}`} onClick={()=>update('checkInFrequency',o.v)}>
                  <div className="option-card-icon">{o.icon}</div>
                  <div className="option-card-label">{o.label}</div>
                  <div className="option-card-desc">{o.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Conversation Style</label>
            <div className="option-cards">
              {[{v:'minimal',icon:'🤐',label:'Minimal',desc:'Just the essentials'},{v:'friendly',icon:'😊',label:'Friendly',desc:'Warm & professional'},{v:'chatty',icon:'💬',label:'Chatty',desc:'Love to talk!'}].map(o=>(
                <div key={o.v} className={`option-card ${form.conversationStyle===o.v?'active':''}`} onClick={()=>update('conversationStyle',o.v)}>
                  <div className="option-card-icon">{o.icon}</div>
                  <div className="option-card-label">{o.label}</div>
                  <div className="option-card-desc">{o.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Allergens</label>
            <div className="allergen-tags">
              {ALLERGENS.map(a=>(
                <span key={a} className={`allergen-tag ${form.allergens.includes(a)?'active':''}`} onClick={()=>toggleAllergen(a)}>{a}</span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Dietary Notes</label>
            <textarea className="form-textarea" value={form.dietaryNotes} onChange={e=>update('dietaryNotes',e.target.value)} placeholder="Vegetarian, keto, no spice, etc." />
          </div>

          <div className="form-group">
            <label className="form-label">Custom Notes for Server</label>
            <textarea className="form-textarea" value={form.customNotes} onChange={e=>update('customNotes',e.target.value)} placeholder="Celebrating an anniversary, prefer booth seating, etc." />
          </div>

          <div style={{margin:'24px 0 8px',paddingTop:20,borderTop:'2px solid var(--cream-dark)'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
              <span style={{background:'var(--amber)',color:'#fff',fontSize:'0.7rem',fontWeight:700,padding:'3px 10px',borderRadius:20,letterSpacing:'0.5px'}}>HOST</span>
              <span style={{fontWeight:700,fontSize:'1rem'}}>Seating Preferences</span>
            </div>
            <div style={{fontSize:'0.85rem',color:'var(--gray)',marginBottom:16}}>Shown to the host when they pull up your reservation link</div>
          </div>

          <div className="form-group">
            <label className="form-label">Seating Type</label>
            <div className="allergen-tags">
              {SEATING_OPTIONS.map(o=>(
                <span key={o.v} className={`allergen-tag ${form.seatingPreferences.includes(o.v)?'active':''}`} onClick={()=>toggleSeating(o.v)}>
                  {o.icon} {o.label}
                </span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Additional Host Notes</label>
            <textarea className="form-textarea" value={form.hostNotes} onChange={e=>update('hostNotes',e.target.value)} placeholder="e.g. No row tables, need space for a stroller, celebrating a birthday — balloon OK" />
          </div>

          <button className="btn btn-primary btn-block" disabled={saving}>
            {saving ? 'Saving...' : (isNew ? 'Create Profile' : 'Save Changes')}
          </button>
        </form>
      </div>
    </div>
  );
}

// Profile Detail / Share View
function ProfileDetail() {
  const navigate = useNavigate();
  const { id: profileId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [toast, setToast] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.getProfile(profileId).then(setProfile).catch(()=>navigate('/profiles')).finally(()=>setLoading(false));
  }, [profileId, navigate]);

  useEffect(() => {
    if (profile?.share_code) {
      import('qrcode').then(QRCode => {
        QRCode.toDataURL(`https://tipnotes.com/s/${profile.share_code}`, {
          width: 200, margin: 2, color: { dark: '#2c2c2c', light: '#ffffff' }
        }).then(setQrDataUrl);
      });
    }
  }, [profile]);

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(''),2500); };

  const copyUrl = () => {
    navigator.clipboard.writeText(`https://tipnotes.com/s/${profile.share_code}`);
    showToast('Link copied!');
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this profile? This cannot be undone.')) return;
    setDeleting(true);
    try { await api.deleteProfile(profileId); navigate('/profiles'); }
    catch (err) { alert(err.message); setDeleting(false); }
  };

  if (loading) return <div className="spinner"></div>;
  if (!profile) return null;

  const shareUrl = `https://tipnotes.com/s/${profile.share_code}`;
  const { user } = useAuth();
  const cleanUrl = (user?.username && profile.slug)
    ? `https://tipnotes.com/${user.username}/${profile.slug}`
    : null;

  return (
    <div style={{maxWidth:560,margin:'0 auto'}}>
      <button className="btn btn-secondary btn-sm" onClick={()=>navigate('/profiles')} style={{marginBottom:16}}>
        ← All Profiles
      </button>

      <div className="card">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16}}>
          <div>
            <h2 className="card-title" style={{marginBottom:4}}>{profile.profile_name}</h2>
            <p style={{color:'var(--gray)',fontSize:'0.9rem'}}>Display name: {profile.display_name}</p>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-secondary btn-sm" onClick={()=>navigate(`/profile/${profileId}/edit`)}>Edit</button>
            <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>Delete</button>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:24}}>
          <div style={{textAlign:'center',padding:12,background:'var(--cream)',borderRadius:10}}>
            <div style={{fontSize:'1.3rem',marginBottom:4}}>{profile.pace==='relaxed'?'🐢':profile.pace==='quick'?'⚡':'🍽️'}</div>
            <div style={{fontSize:'0.8rem',color:'var(--gray)'}}>Pace</div>
            <div style={{fontWeight:600,fontSize:'0.9rem',textTransform:'capitalize'}}>{profile.pace}</div>
          </div>
          <div style={{textAlign:'center',padding:12,background:'var(--cream)',borderRadius:10}}>
            <div style={{fontSize:'1.3rem',marginBottom:4}}>{profile.check_in_frequency==='minimal'?'🤫':profile.check_in_frequency==='frequent'?'🔔':'👋'}</div>
            <div style={{fontSize:'0.8rem',color:'var(--gray)'}}>Check-ins</div>
            <div style={{fontWeight:600,fontSize:'0.9rem',textTransform:'capitalize'}}>{profile.check_in_frequency}</div>
          </div>
          <div style={{textAlign:'center',padding:12,background:'var(--cream)',borderRadius:10}}>
            <div style={{fontSize:'1.3rem',marginBottom:4}}>{profile.conversation_style==='minimal'?'🤐':profile.conversation_style==='chatty'?'💬':'😊'}</div>
            <div style={{fontSize:'0.8rem',color:'var(--gray)'}}>Conversation</div>
            <div style={{fontWeight:600,fontSize:'0.9rem',textTransform:'capitalize'}}>{profile.conversation_style}</div>
          </div>
        </div>

        {profile.allergens?.length > 0 && (
          <div style={{marginBottom:16}}>
            <div style={{fontWeight:600,fontSize:'0.85rem',marginBottom:6}}>⚠️ Allergens</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {profile.allergens.map(a=><span key={a} style={{background:'#fff0f0',color:'var(--red)',padding:'4px 12px',borderRadius:16,fontSize:'0.8rem',fontWeight:600}}>{a}</span>)}
            </div>
          </div>
        )}
        {profile.dietary_notes && <div style={{marginBottom:16}}><div style={{fontWeight:600,fontSize:'0.85rem',marginBottom:6}}>🥗 Dietary Notes</div><p style={{fontSize:'0.9rem',color:'var(--gray)'}}>{profile.dietary_notes}</p></div>}
        {profile.custom_notes && <div style={{marginBottom:16}}><div style={{fontWeight:600,fontSize:'0.85rem',marginBottom:6}}>📝 Custom Notes</div><p style={{fontSize:'0.9rem',color:'var(--gray)'}}>{profile.custom_notes}</p></div>}

        {(profile.seating_preferences?.length > 0 || profile.host_notes) && (
          <div style={{marginBottom:16,paddingTop:16,borderTop:'1px solid var(--gray-light)'}}>
            <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
              <span style={{background:'var(--amber)',color:'#fff',fontSize:'0.7rem',fontWeight:700,padding:'2px 8px',borderRadius:20,letterSpacing:'0.5px'}}>HOST</span>
              <div style={{fontWeight:600,fontSize:'0.85rem'}}>Seating Preferences</div>
            </div>
            {profile.seating_preferences?.length > 0 && (
              <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom: profile.host_notes ? 8 : 0}}>
                {profile.seating_preferences.map(s => {
                  const opt = {booth:'🪑 Booth',table:'🍽️ Table',bar:'🍸 Bar',patio:'🌿 Patio',window:'🪟 Window','quiet':'🔇 Quiet Corner','away-kitchen':'🚫 Away from Kitchen','away-bathroom':'🚽 Away from Bathrooms','high-top':'🪑 High Top',accessible:'♿ Accessible'};
                  return <span key={s} style={{background:'var(--cream-dark)',padding:'4px 12px',borderRadius:16,fontSize:'0.8rem',fontWeight:600}}>{opt[s]||s}</span>;
                })}
              </div>
            )}
            {profile.host_notes && <p style={{fontSize:'0.9rem',color:'var(--gray)'}}>{profile.host_notes}</p>}
          </div>
        )}

        <div style={{marginBottom:8}}><div style={{fontWeight:600,fontSize:'0.85rem',marginBottom:4}}>📊 Views</div><p style={{fontSize:'0.9rem',color:'var(--amber)',fontWeight:600}}>{profile.view_count || 0} times viewed by servers</p></div>
      </div>

      <div className="card">
        <h3 className="card-title">Share This Profile</h3>

        {cleanUrl && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:'0.8rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--amber)',marginBottom:4}}>✨ Clean Link</div>
            <div className="share-url">
              <input readOnly value={cleanUrl} />
              <button className="copy-btn" onClick={()=>{navigator.clipboard.writeText(cleanUrl);showToast('Clean link copied!');}}>Copy</button>
            </div>
            <div style={{fontSize:'0.75rem',color:'var(--gray)',marginTop:4}}>Use this in reservation notes — easy to read and remember</div>
          </div>
        )}

        <div style={{marginBottom:16}}>
          <div style={{fontSize:'0.8rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--gray)',marginBottom:8}}>QR / Short Link</div>
          <div style={{display:'flex',gap:16,alignItems:'flex-start'}}>
            {qrDataUrl && (
              <img src={qrDataUrl} alt="QR Code" style={{width:90,height:90,borderRadius:8,boxShadow:'var(--shadow)',flexShrink:0,cursor:'pointer'}}
                onClick={()=>{const w=window.open('','_blank');w.document.write(`<html><head><title>TipNotes QR</title></head><body style="display:flex;align-items:center;justify-content:center;min-height:100vh;"><img src="${qrDataUrl}" style="width:400px;"/></body></html>`);w.document.close();w.print();}}
                title="Click to print" />
            )}
            <div style={{flex:1}}>
              <div className="share-url" style={{marginBottom:6}}>
                <input readOnly value={shareUrl} />
                <button className="copy-btn" onClick={copyUrl}>Copy</button>
              </div>
              <div style={{fontSize:'0.75rem',color:'var(--gray)'}}>Used for QR codes — always works even if you change your username</div>
            </div>
          </div>
        </div>

        {!cleanUrl && (
          <div className="alert alert-info" style={{marginBottom:12,fontSize:'0.85rem'}}>
            💡 Set a username in your <a href="/account" style={{color:'var(--amber-dark)',fontWeight:600}}>Account settings</a> to get a clean shareable link like tipnotes.com/yourname/profile-name
          </div>
        )}

        <div className="share-methods" style={{gridTemplateColumns:'1fr 1fr',marginTop:8}}>
          <div className="share-method" onClick={()=>{const w=window.open('','_blank');w.document.write(`<html><head><title>TipNotes QR</title></head><body style="display:flex;align-items:center;justify-content:center;min-height:100vh;"><img src="${qrDataUrl}" style="width:400px;"/></body></html>`);w.document.close();w.print();}}>
            <div className="share-method-icon">🖨️</div>
            <div className="share-method-label">Print QR Code</div>
            <div className="share-method-desc">Print for your wallet or table</div>
          </div>
          <div className="share-method" onClick={()=>{window.open(`/s/${profile.share_code}`,'_blank')}}>
            <div className="share-method-icon">📱</div>
            <div className="share-method-label">Show on Phone</div>
            <div className="share-method-desc">Show your server directly</div>
          </div>
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

// Waiter-Facing Shared View
function SharedView() {
  const shareCode = window.location.pathname.split('/s/')[1];
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getSharedProfile(shareCode).then(setProfile).catch(e=>setError(e.message)).finally(()=>setLoading(false));
  }, [shareCode]);

  if (loading) return <div className="app-wrapper"><div className="spinner"></div></div>;
  if (error) return (
    <div className="app-wrapper" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh'}}>
      <div className="card" style={{textAlign:'center',maxWidth:400}}>
        <div style={{fontSize:'3rem',marginBottom:12}}>🍽️</div>
        <h2 className="card-title">Profile Not Found</h2>
        <p style={{color:'var(--gray)'}}>This link may be expired or invalid.</p>
      </div>
    </div>
  );

  const paceMap = {relaxed:{icon:'🐢',text:'Relaxed — No rush, enjoying the evening'},moderate:{icon:'🍽️',text:'Moderate — Normal dining pace'},quick:{icon:'⚡',text:'Quick — Time-conscious, efficient service'}};
  const checkinMap = {minimal:{icon:'🤫',text:'Minimal — Only check in when needed'},normal:{icon:'👋',text:'Normal — Standard check-ins'},frequent:{icon:'🔔',text:'Frequent — Please check in often'}};
  const convMap = {minimal:{icon:'🤐',text:'Minimal — Just the essentials please'},friendly:{icon:'😊',text:'Friendly — Warm & professional'},chatty:{icon:'💬',text:'Chatty — Love to chat!'}};

  return (
    <div style={{minHeight:'100vh',background:'linear-gradient(180deg,var(--cream) 0%,#ede0d0 100%)',padding:'32px 16px'}}>
      <div className="waiter-card">
        <div className="waiter-card-header">
          <div style={{display:'flex',justifyContent:'center',marginBottom:8}}>
            <svg viewBox="0 0 64 64" width="32" height="32" fill="none"><rect x="16" y="8" width="32" height="48" rx="4" fill="#d4760a"/><line x1="24" y1="20" x2="40" y2="20" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><line x1="24" y1="28" x2="40" y2="28" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><line x1="24" y1="36" x2="34" y2="36" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><path d="M44 30 L48 6 L50 6 L46 30Z" fill="#555"/><path d="M48 6 L49 2 L50 6Z" fill="#d4760a"/></svg>
          </div>
          <div className="waiter-card-name">{profile.display_name}</div>
          <div className="waiter-card-badge">DINING PREFERENCES</div>
        </div>

        {/* HOST SECTION */}
        {(profile.seating_preferences?.length > 0 || profile.host_notes) && (
          <div style={{background:'#fffbf0',border:'2px solid var(--amber)',borderRadius:12,padding:'14px 16px',marginBottom:20}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
              <span style={{background:'var(--amber)',color:'#fff',fontSize:'0.7rem',fontWeight:700,padding:'3px 10px',borderRadius:20,letterSpacing:'0.5px'}}>HOST</span>
              <span style={{fontWeight:700,fontSize:'0.9rem'}}>Seating Request</span>
            </div>
            {profile.seating_preferences?.length > 0 && (
              <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom: profile.host_notes ? 10 : 0}}>
                {profile.seating_preferences.map(s => {
                  const opt = {booth:'🪑 Booth',table:'🍽️ Table',bar:'🍸 Bar',patio:'🌿 Patio/Outside',window:'🪟 Window Seat',quiet:'🔇 Quiet Corner','away-kitchen':'🚫 Away from Kitchen','away-bathroom':'🚽 Away from Bathrooms','high-top':'🪑 High Top',accessible:'♿ Accessible'};
                  return <span key={s} style={{background:'var(--cream-dark)',padding:'4px 12px',borderRadius:16,fontSize:'0.82rem',fontWeight:600}}>{opt[s]||s}</span>;
                })}
              </div>
            )}
            {profile.host_notes && <div style={{fontSize:'0.9rem',lineHeight:1.5}}>{profile.host_notes}</div>}
          </div>
        )}

        <div className="waiter-section">
          <div className="waiter-section-title">Service Style</div>
          <div className="waiter-pref">
            <span className="waiter-pref-icon">{paceMap[profile.pace]?.icon}</span>
            <div><div className="waiter-pref-value">{paceMap[profile.pace]?.text}</div></div>
          </div>
          <div className="waiter-pref">
            <span className="waiter-pref-icon">{checkinMap[profile.check_in_frequency]?.icon}</span>
            <div><div className="waiter-pref-value">{checkinMap[profile.check_in_frequency]?.text}</div></div>
          </div>
          <div className="waiter-pref">
            <span className="waiter-pref-icon">{convMap[profile.conversation_style]?.icon}</span>
            <div><div className="waiter-pref-value">{convMap[profile.conversation_style]?.text}</div></div>
          </div>
        </div>

        {profile.allergens?.length > 0 && (
          <div className="waiter-section">
            <div className="waiter-section-title">⚠️ Allergens</div>
            <div className="waiter-allergens">
              {profile.allergens.map(a=><span key={a} className="waiter-allergen-tag">{a}</span>)}
            </div>
          </div>
        )}

        {profile.dietary_notes && (
          <div className="waiter-section">
            <div className="waiter-section-title">Dietary Notes</div>
            <div className="waiter-notes">{profile.dietary_notes}</div>
          </div>
        )}

        {profile.custom_notes && (
          <div className="waiter-section">
            <div className="waiter-section-title">Notes</div>
            <div className="waiter-notes">{profile.custom_notes}</div>
          </div>
        )}

        <div style={{textAlign:'center',marginTop:20,paddingTop:16,borderTop:'1px solid var(--cream-dark)'}}>
          <span style={{fontSize:'0.75rem',color:'var(--gray)'}}>Powered by </span>
          <a href="https://tipnotes.com" style={{fontSize:'0.75rem',color:'var(--amber)',fontWeight:600,textDecoration:'none'}}>TipNotes</a>
        </div>
      </div>
    </div>
  );
}

// User Account / Profile Page
function UserAccount() {
  const { user, setUser } = useAuth();

  // Profile edit state
  const [infoForm, setInfoForm] = useState({ firstName: user.firstName || '', lastName: user.lastName || '', email: user.email || '', username: user.username || '' });
  const [infoEditing, setInfoEditing] = useState(false);
  const [infoError, setInfoError] = useState('');
  const [infoSuccess, setInfoSuccess] = useState('');
  const [infoLoading, setInfoLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null);

  // Password state
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  const updateInfo = (k, v) => setInfoForm(f => ({ ...f, [k]: v }));
  const updatePw = (k, v) => setPwForm(f => ({ ...f, [k]: v }));

  const checkUsername = async (val) => {
    if (!val || val === user.username) { setUsernameStatus(null); return; }
    if (val.length < 3 || !/^[a-z0-9_-]{3,30}$/i.test(val)) { setUsernameStatus('invalid'); return; }
    setUsernameStatus('checking');
    try {
      const res = await api.checkUsername(val);
      setUsernameStatus(res.available ? 'available' : 'taken');
    } catch { setUsernameStatus(null); }
  };

  const handleUpdateInfo = async (e) => {
    e.preventDefault();
    setInfoError(''); setInfoSuccess('');
    setInfoLoading(true);
    try {
      const updated = await api.updateMe(infoForm);
      setUser(updated);
      setInfoEditing(false);
      setUsernameStatus(null);
      setInfoSuccess(updated.email !== user.email ? 'Profile updated! Please verify your new email address.' : 'Profile updated successfully!');
    } catch (err) {
      setInfoError(err.message);
    }
    setInfoLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    if (pwForm.newPassword !== pwForm.confirmPassword) return setPwError('New passwords do not match');
    if (pwForm.newPassword.length < 8) return setPwError('New password must be at least 8 characters');
    setPwLoading(true);
    try {
      await api.changePassword(pwForm.currentPassword, pwForm.newPassword);
      setPwSuccess('Password changed successfully!');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwError(err.message);
    }
    setPwLoading(false);
  };

  return (
    <div style={{maxWidth:480,margin:'0 auto'}}>
      <h1 style={{fontFamily:'var(--font-display)',fontSize:'1.8rem',marginBottom:24}}>My Account</h1>

      {/* Profile Info */}
      <div className="card" style={{marginBottom:16}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <h2 className="card-title" style={{margin:0}}>Profile</h2>
          {!infoEditing && (
            <button className="btn btn-secondary btn-sm" onClick={()=>{setInfoEditing(true);setInfoSuccess('');setInfoError('');}}>
              Edit
            </button>
          )}
        </div>
        {infoError && <div className="alert alert-error">{infoError}</div>}
        {infoSuccess && <div className="alert alert-success">{infoSuccess}</div>}
        {infoEditing ? (
          <form onSubmit={handleUpdateInfo}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">First Name</label>
                <input className="form-input" value={infoForm.firstName} onChange={e=>updateInfo('firstName',e.target.value)} required />
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Last Name</label>
                <input className="form-input" value={infoForm.lastName} onChange={e=>updateInfo('lastName',e.target.value)} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input className="form-input" value={infoForm.username}
                onChange={e=>{updateInfo('username',e.target.value.toLowerCase());checkUsername(e.target.value);}}
                placeholder="e.g. mark" maxLength={30} />
              {usernameStatus === 'checking' && <div className="form-hint">Checking...</div>}
              {usernameStatus === 'available' && <div className="form-hint" style={{color:'var(--green)'}}>✓ Available! tipnotes.com/{infoForm.username}/profile-name</div>}
              {usernameStatus === 'taken' && <div className="form-error">Username already taken</div>}
              {usernameStatus === 'invalid' && <div className="form-error">3–30 characters, letters/numbers/hyphens/underscores only</div>}
              {!usernameStatus && infoForm.username && <div className="form-hint">Your URL: tipnotes.com/{infoForm.username}/profile-name</div>}
              {!usernameStatus && !infoForm.username && <div className="form-hint">Creates clean links like tipnotes.com/yourname/date-night</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" value={infoForm.email} onChange={e=>updateInfo('email',e.target.value)} required />
              <div className="form-hint">Changing your email will require re-verification.</div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary" disabled={infoLoading}>{infoLoading ? 'Saving...' : 'Save Changes'}</button>
              <button type="button" className="btn btn-secondary" onClick={()=>{setInfoEditing(false);setInfoForm({firstName:user.firstName,lastName:user.lastName,email:user.email,username:user.username||''});setInfoError('');setUsernameStatus(null);}}>Cancel</button>
            </div>
          </form>
        ) : (
          <>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
              <div>
                <div style={{fontSize:'0.8rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--gray)',marginBottom:4}}>First Name</div>
                <div style={{fontWeight:600,fontSize:'1.05rem'}}>{user.firstName}</div>
              </div>
              <div>
                <div style={{fontSize:'0.8rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--gray)',marginBottom:4}}>Last Name</div>
                <div style={{fontWeight:600,fontSize:'1.05rem'}}>{user.lastName}</div>
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:'0.8rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--gray)',marginBottom:4}}>Username</div>
              {user.username
                ? <div style={{fontWeight:600,fontSize:'1.05rem',color:'var(--amber)'}}>@{user.username} <span style={{fontWeight:400,fontSize:'0.85rem',color:'var(--gray)'}}>→ tipnotes.com/{user.username}/...</span></div>
                : <div style={{fontSize:'0.9rem',color:'var(--gray)'}}>Not set — <button className="btn btn-secondary btn-sm" style={{display:'inline',padding:'2px 10px',fontSize:'0.8rem'}} onClick={()=>{setInfoEditing(true);setInfoSuccess('');setInfoError('');}}>Add username</button></div>}
            </div>
            <div>
              <div style={{fontSize:'0.8rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--gray)',marginBottom:4}}>Email Address</div>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{fontWeight:600,fontSize:'1.05rem'}}>{user.email}</span>
                {user.emailVerified
                  ? <span className="badge badge-green">✓ Verified</span>
                  : <span className="badge badge-gray">Unverified</span>}
              </div>
            </div>
            {user.isAdmin && (
              <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid var(--gray-light)'}}>
                <span className="badge badge-amber">⚙️ Administrator</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Change Password */}
      <div className="card">
        <h2 className="card-title">Change Password</h2>
        {pwError && <div className="alert alert-error">{pwError}</div>}
        {pwSuccess && <div className="alert alert-success">{pwSuccess}</div>}
        <form onSubmit={handleChangePassword}>
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input className="form-input" type="password" value={pwForm.currentPassword}
              onChange={e => updatePw('currentPassword', e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input className="form-input" type="password" value={pwForm.newPassword}
              onChange={e => updatePw('newPassword', e.target.value)} required minLength={8} />
            <div className="form-hint">At least 8 characters</div>
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input className="form-input" type="password" value={pwForm.confirmPassword}
              onChange={e => updatePw('confirmPassword', e.target.value)} required />
          </div>
          <button className="btn btn-primary" disabled={pwLoading}>
            {pwLoading ? 'Saving...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Admin Panel
function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!user?.isAdmin) { navigate('/profiles'); return; }
    Promise.all([api.getAdminStats(), api.getAdminUsers()])
      .then(([s, u]) => { setStats(s); setUsers(u); })
      .catch(err => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const handleToggleAdmin = async (id, currentStatus) => {
    if (!window.confirm(`${currentStatus ? 'Remove' : 'Grant'} admin access for this user?`)) return;
    try {
      const result = await api.toggleAdmin(id);
      setUsers(u => u.map(x => x.id === id ? { ...x, isAdmin: result.isAdmin } : x));
    } catch (err) { alert(err.message); }
  };

  const handleDeleteUser = async (id, email) => {
    if (!window.confirm(`Delete user ${email}? This will also delete all their profiles. This cannot be undone.`)) return;
    try {
      await api.deleteUser(id);
      setUsers(u => u.filter(x => x.id !== id));
    } catch (err) { alert(err.message); }
  };

  if (loading) return <div className="spinner"></div>;
  if (loadError) return <div className="alert alert-error" style={{marginTop:24}}>Failed to load admin data: {loadError}</div>;

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24}}>
        <h1 style={{fontFamily:'var(--font-display)',fontSize:'1.8rem'}}>⚙️ Admin Panel</h1>
      </div>

      <div style={{display:'flex',gap:8,marginBottom:24,borderBottom:'2px solid var(--gray-light)',paddingBottom:0}}>
        {['overview','users'].map(tab => (
          <button key={tab} onClick={()=>setActiveTab(tab)}
            style={{padding:'8px 20px',border:'none',background:'none',cursor:'pointer',
              fontFamily:'var(--font-body)',fontWeight:600,fontSize:'0.9rem',
              color:activeTab===tab?'var(--amber)':'var(--gray)',
              borderBottom:activeTab===tab?'2px solid var(--amber)':'2px solid transparent',
              marginBottom:-2,textTransform:'capitalize'}}>
            {tab === 'overview' ? '📊 Overview' : '👥 Users'}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && stats && (
        <div>
          <div className="admin-stats">
            <div className="admin-stat">
              <div className="admin-stat-value">{stats.totalUsers}</div>
              <div className="admin-stat-label">Total Users</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-value">{stats.verifiedUsers}</div>
              <div className="admin-stat-label">Verified</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-value">{stats.newUsersLast7Days}</div>
              <div className="admin-stat-label">New This Week</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-value">{stats.totalProfiles}</div>
              <div className="admin-stat-label">Total Profiles</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-value">{stats.activeProfiles}</div>
              <div className="admin-stat-label">Active Profiles</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-value">{stats.viewsLast30Days}</div>
              <div className="admin-stat-label">Views (30 Days)</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div style={{padding:'16px 20px',borderBottom:'1px solid var(--gray-light)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontWeight:700}}>{users.length} users</span>
          </div>
          <div style={{overflowX:'auto'}}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Profiles</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td style={{fontWeight:600}}>{u.firstName} {u.lastName}{u.isAdmin && <span className="badge badge-amber" style={{marginLeft:6}}>Admin</span>}</td>
                    <td style={{color:'var(--gray)',fontSize:'0.85rem'}}>{u.email}</td>
                    <td>
                      {u.emailVerified
                        ? <span className="badge badge-green">✓ Verified</span>
                        : <span className="badge badge-gray">Unverified</span>}
                    </td>
                    <td style={{textAlign:'center'}}>{u.profileCount}</td>
                    <td style={{color:'var(--gray)',fontSize:'0.85rem'}}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div style={{display:'flex',gap:6}}>
                        {u.id !== user.id && (
                          <>
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => handleToggleAdmin(u.id, u.isAdmin)}
                              style={{fontSize:'0.75rem',padding:'4px 10px'}}>
                              {u.isAdmin ? 'Remove Admin' : 'Make Admin'}
                            </button>
                            <button className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteUser(u.id, u.email)}
                              style={{fontSize:'0.75rem',padding:'4px 10px'}}>
                              Delete
                            </button>
                          </>
                        )}
                        {u.id === user.id && <span style={{fontSize:'0.8rem',color:'var(--gray)'}}>You</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Username/Slug Public View (clean URLs)
function UsernameView() {
  const { username, slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getProfileByUsername(username, slug).then(setProfile).catch(e=>setError(e.message)).finally(()=>setLoading(false));
  }, [username, slug]);

  if (loading) return <div className="app-wrapper"><div className="spinner"></div></div>;
  if (error) return (
    <div className="app-wrapper" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh'}}>
      <div className="card" style={{textAlign:'center',maxWidth:400}}>
        <div style={{fontSize:'3rem',marginBottom:12}}>🍽️</div>
        <h2 className="card-title">Profile Not Found</h2>
        <p style={{color:'var(--gray)'}}>This link may be expired or invalid.</p>
      </div>
    </div>
  );

  const paceMap = {relaxed:{icon:'🐢',text:'Relaxed — No rush, enjoying the evening'},moderate:{icon:'🍽️',text:'Moderate — Normal dining pace'},quick:{icon:'⚡',text:'Quick — Time-conscious, efficient service'}};
  const checkinMap = {minimal:{icon:'🤫',text:'Minimal — Only check in when needed'},normal:{icon:'👋',text:'Normal — Standard check-ins'},frequent:{icon:'🔔',text:'Frequent — Please check in often'}};
  const convMap = {minimal:{icon:'🤐',text:'Minimal — Just the essentials please'},friendly:{icon:'😊',text:'Friendly — Warm & professional'},chatty:{icon:'💬',text:'Chatty — Love to chat!'}};

  return (
    <div style={{minHeight:'100vh',background:'linear-gradient(180deg,var(--cream) 0%,#ede0d0 100%)',padding:'32px 16px'}}>
      <div className="waiter-card">
        <div className="waiter-card-header">
          <div style={{display:'flex',justifyContent:'center',marginBottom:8}}>
            <svg viewBox="0 0 64 64" width="32" height="32" fill="none"><rect x="16" y="8" width="32" height="48" rx="4" fill="#d4760a"/><line x1="24" y1="20" x2="40" y2="20" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><line x1="24" y1="28" x2="40" y2="28" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><line x1="24" y1="36" x2="34" y2="36" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><path d="M44 30 L48 6 L50 6 L46 30Z" fill="#555"/><path d="M48 6 L49 2 L50 6Z" fill="#d4760a"/></svg>
          </div>
          <div className="waiter-card-name">{profile.display_name}</div>
          <div className="waiter-card-badge">DINING PREFERENCES</div>
        </div>

        {/* HOST SECTION */}
        {(profile.seating_preferences?.length > 0 || profile.host_notes) && (
          <div style={{background:'#fffbf0',border:'2px solid var(--amber)',borderRadius:12,padding:'14px 16px',marginBottom:20}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
              <span style={{background:'var(--amber)',color:'#fff',fontSize:'0.7rem',fontWeight:700,padding:'3px 10px',borderRadius:20,letterSpacing:'0.5px'}}>HOST</span>
              <span style={{fontWeight:700,fontSize:'0.9rem'}}>Seating Request</span>
            </div>
            {profile.seating_preferences?.length > 0 && (
              <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom: profile.host_notes ? 10 : 0}}>
                {profile.seating_preferences.map(s => {
                  const opt = {booth:'🪑 Booth',table:'🍽️ Table',bar:'🍸 Bar',patio:'🌿 Patio/Outside',window:'🪟 Window Seat',quiet:'🔇 Quiet Corner','away-kitchen':'🚫 Away from Kitchen','away-bathroom':'🚽 Away from Bathrooms','high-top':'🪑 High Top',accessible:'♿ Accessible'};
                  return <span key={s} style={{background:'var(--cream-dark)',padding:'4px 12px',borderRadius:16,fontSize:'0.82rem',fontWeight:600}}>{opt[s]||s}</span>;
                })}
              </div>
            )}
            {profile.host_notes && <div style={{fontSize:'0.9rem',lineHeight:1.5}}>{profile.host_notes}</div>}
          </div>
        )}

        <div className="waiter-section">
          <div className="waiter-section-title">Service Style</div>
          <div className="waiter-pref"><span className="waiter-pref-icon">{paceMap[profile.pace]?.icon}</span><div><div className="waiter-pref-value">{paceMap[profile.pace]?.text}</div></div></div>
          <div className="waiter-pref"><span className="waiter-pref-icon">{checkinMap[profile.check_in_frequency]?.icon}</span><div><div className="waiter-pref-value">{checkinMap[profile.check_in_frequency]?.text}</div></div></div>
          <div className="waiter-pref"><span className="waiter-pref-icon">{convMap[profile.conversation_style]?.icon}</span><div><div className="waiter-pref-value">{convMap[profile.conversation_style]?.text}</div></div></div>
        </div>
        {profile.allergens?.length > 0 && (
          <div className="waiter-section">
            <div className="waiter-section-title">⚠️ Allergens</div>
            <div className="waiter-allergens">{profile.allergens.map(a=><span key={a} className="waiter-allergen-tag">{a}</span>)}</div>
          </div>
        )}
        {profile.dietary_notes && (<div className="waiter-section"><div className="waiter-section-title">Dietary Notes</div><div className="waiter-notes">{profile.dietary_notes}</div></div>)}
        {profile.custom_notes && (<div className="waiter-section"><div className="waiter-section-title">Notes</div><div className="waiter-notes">{profile.custom_notes}</div></div>)}
        <div style={{textAlign:'center',marginTop:20,paddingTop:16,borderTop:'1px solid var(--cream-dark)'}}>
          <span style={{fontSize:'0.75rem',color:'var(--gray)'}}>Powered by </span>
          <a href="https://tipnotes.com" style={{fontSize:'0.75rem',color:'var(--amber)',fontWeight:600,textDecoration:'none'}}>TipNotes</a>
        </div>
      </div>
    </div>
  );
}

// Protected Route Wrapper
function ProtectedRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
}

// Main App
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Shared view — no header/chrome */}
          <Route path="/s/:code" element={<SharedView />} />

          {/* All other routes with header */}
          <Route path="*" element={
            <div className="app-wrapper">
              <Header />
              <div className="main-content">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/profiles" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/profile/new" element={<ProtectedRoute><ProfileEditor /></ProtectedRoute>} />
                  <Route path="/profile/:id/edit" element={<ProtectedRoute><ProfileEditor /></ProtectedRoute>} />
                  <Route path="/profile/:id" element={<ProtectedRoute><ProfileDetail /></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
                  <Route path="/account" element={<ProtectedRoute><UserAccount /></ProtectedRoute>} />
                  {/* Clean username URLs — must come AFTER all named app routes */}
                  <Route path="/:username/:slug" element={<UsernameView />} />
                </Routes>
              </div>
              <footer style={{textAlign:'center',padding:'24px 16px',borderTop:'1px solid var(--gray-light)',marginTop:'auto',fontSize:'0.85rem',color:'var(--gray)'}}>
                © {new Date().getFullYear()} TipNotes. All rights reserved.
              </footer>
            </div>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
